import { useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { ChevronDown, ChevronUp, Database, Loader2, Play, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  useSources,
  useCreateSource,
  useDeleteSource,
  useTriggerIngest,
} from '@/hooks/queries'
import type { Source, SourceType, SourceSchedule } from '@/lib/types'
import { TierBadge } from '@/components/stories/TierBadge'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function typeBadgeClass(type: SourceType) {
  switch (type) {
    case 'rss':
      return 'bg-blue-500/15 text-blue-400 border-blue-500/30'
    case 'api':
      return 'bg-purple-500/15 text-purple-400 border-purple-500/30'
    case 'scraper':
      return 'bg-orange-500/15 text-orange-400 border-orange-500/30'
    case 'manual':
      return 'bg-muted text-muted-foreground'
  }
}

/** KAN-854 — the API's derived label, or null for an ordinary registry row. */
function dedicatedJob(source: Source) {
  return source.management?.kind === 'dedicated_job' ? source.management : null
}

/** "Fetch now" is refused (409) when the registry schedule does not drive the job. */
function fetchNowUnavailable(source: Source): boolean {
  const job = dedicatedJob(source)
  return source.type === 'manual' || (job !== null && !job.registryScheduleDrivesJob)
}

function DedicatedJobBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {label}
    </span>
  )
}

function truncateUrl(url: string, max = 50) {
  return url.length > max ? url.slice(0, max) + '…' : url
}

// ─── Source detail modal ──────────────────────────────────────────────────────

interface SourceDetailModalProps {
  source: Source | null
  onClose: () => void
}

function SourceDetailModal({ source, onClose }: SourceDetailModalProps) {
  const deleteSource = useDeleteSource()

  return (
    <DialogPrimitive.Root open={Boolean(source)} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b px-6 py-4">
            <div>
              <DialogPrimitive.Title className="text-base font-semibold">
                {source?.name}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-xs text-muted-foreground mt-0.5">
                Source details
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </DialogPrimitive.Close>
          </div>

          {/* Body */}
          {source && (
            <div className="flex flex-col gap-4 px-6 py-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {dedicatedJob(source) && (
                  <div className="col-span-2 flex flex-col gap-1">
                    <DedicatedJobBadge label={dedicatedJob(source)!.label} />
                    <p className="text-xs text-muted-foreground">{dedicatedJob(source)!.detail}</p>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">Type</span>
                  <p className="font-medium mt-0.5 uppercase">{source.type}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Schedule</span>
                  <p className="font-medium mt-0.5 capitalize">{source.schedule ?? '—'}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">URL</span>
                  <p className="font-medium mt-0.5 break-all text-xs">{source.url ?? '—'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status</span>
                  <p className="font-medium mt-0.5">{source.enabled ? 'Active' : 'Inactive'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Error Count</span>
                  <p className="font-medium mt-0.5">{source.errorCount}</p>
                </div>
                {source.lastFetchedAt && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Last Fetched</span>
                    <p className="font-medium mt-0.5 text-xs">{new Date(source.lastFetchedAt).toLocaleString()}</p>
                  </div>
                )}
                {source.lastError && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Last Error</span>
                    <p className="font-medium mt-0.5 text-xs text-red-400">{source.lastError}</p>
                  </div>
                )}
                {source.config && Object.keys(source.config).length > 0 && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Config</span>
                    <pre className="font-mono mt-0.5 text-xs bg-muted/30 rounded p-2 overflow-auto max-h-32">
                      {JSON.stringify(source.config, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-between gap-2 border-t px-6 py-4">
            <Button
              variant="destructive"
              size="sm"
              disabled={deleteSource.isPending}
              onClick={() => {
                if (source) {
                  deleteSource.mutate(source.id, { onSuccess: onClose })
                }
              }}
            >
              {deleteSource.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Delete
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

// ─── Add source panel ─────────────────────────────────────────────────────────

function AddSourcePanel() {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [type, setType] = useState<SourceType>('rss')
  const [url, setUrl] = useState('')
  const [schedule, setSchedule] = useState<SourceSchedule>('daily')
  const [enabled, setEnabled] = useState(true)

  const createSource = useCreateSource()

  function handleAdd() {
    if (!name.trim()) return
    createSource.mutate(
      { name, type, url: url || undefined, schedule, enabled } as Partial<Source>,
      {
        onSuccess: () => {
          setName('')
          setType('rss')
          setUrl('')
          setSchedule('daily')
          setEnabled(true)
          setOpen(false)
        },
      },
    )
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-lg border">
        <CollapsibleTrigger asChild>
          <button className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/30 transition-colors">
            <span className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-muted-foreground" />
              Add a new source
            </span>
            {open ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-col gap-4 border-t px-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. CISA KEV Feed"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Type</label>
                <Select value={type} onValueChange={(v) => setType(v as SourceType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rss">RSS</SelectItem>
                    <SelectItem value="api">API</SelectItem>
                    <SelectItem value="scraper">Scraper</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <label className="text-sm font-medium">URL</label>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/feed"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Schedule</label>
                <Select value={schedule} onValueChange={(v) => setSchedule(v as SourceSchedule)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="6h">Every 6 hours</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Active</label>
                <button
                  type="button"
                  onClick={() => setEnabled((v) => !v)}
                  className={`flex h-9 w-16 items-center rounded-full border-2 px-0.5 transition-colors ${
                    enabled
                      ? 'border-green-500/50 bg-green-500/20'
                      : 'border-muted bg-muted/30'
                  }`}
                  aria-checked={enabled}
                  role="switch"
                >
                  <span
                    className={`h-6 w-6 rounded-full transition-transform ${
                      enabled ? 'translate-x-7 bg-green-400' : 'translate-x-0 bg-muted-foreground'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleAdd} disabled={createSource.isPending || !name.trim()}>
                {createSource.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Add Source
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

// ─── Sources table ────────────────────────────────────────────────────────────

interface SourcesTableProps {
  sources: Source[]
  onViewDetail: (source: Source) => void
}

function SourcesTable({ sources, onViewDetail }: SourcesTableProps) {
  const triggerIngest = useTriggerIngest()

  return (
    <div className="rounded-lg border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/20 text-muted-foreground">
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Type</th>
              <th className="px-4 py-3 text-left font-medium">Tier</th>
              <th className="px-4 py-3 text-left font-medium hidden lg:table-cell">Parent org</th>
              <th className="px-4 py-3 text-left font-medium hidden md:table-cell">URL</th>
              <th className="px-4 py-3 text-left font-medium hidden lg:table-cell">Schedule</th>
              <th className="px-4 py-3 text-center font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium hidden xl:table-cell">Last Fetched</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((source) => (
              <tr key={source.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3">
                  <button
                    className="font-medium hover:underline hover:text-foreground/80 transition-colors text-left"
                    onClick={() => onViewDetail(source)}
                  >
                    {source.name}
                  </button>
                  {dedicatedJob(source) && (
                    <div className="mt-1" title={dedicatedJob(source)!.detail}>
                      <DedicatedJobBadge label={dedicatedJob(source)!.label} />
                    </div>
                  )}
                  {source.lastError && (
                    <p className="text-xs text-red-400 mt-0.5 truncate max-w-[200px]" title={source.lastError}>
                      {source.lastError}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium uppercase ${typeBadgeClass(source.type)}`}
                  >
                    {source.type}
                  </span>
                </td>
                {/* TODO(T16-mutation): wrap in <Select> once backend PUT /sources/:id Zod schema is extended to accept tier + parentOrg. */}
                <td className="px-4 py-3">
                  <TierBadge tier={source.tier} />
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <span className="text-muted-foreground text-xs">
                    {source.parentOrg && source.parentOrg !== 'unknown' ? source.parentOrg : '—'}
                  </span>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <span className="text-muted-foreground text-xs font-mono">
                    {source.url ? truncateUrl(source.url) : '—'}
                  </span>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <span className="text-muted-foreground capitalize text-xs">{source.schedule ?? '—'}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span
                    className={`inline-block h-2.5 w-2.5 rounded-full ${
                      source.enabled ? 'bg-green-400' : 'bg-red-400'
                    }`}
                    title={source.enabled ? 'Active' : 'Inactive'}
                  />
                </td>
                <td className="px-4 py-3 hidden xl:table-cell">
                  <span className="text-muted-foreground text-xs">
                    {source.lastFetchedAt
                      ? new Date(source.lastFetchedAt).toLocaleString()
                      : '—'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={triggerIngest.isPending || fetchNowUnavailable(source)}
                      onClick={() => triggerIngest.mutate(source.id)}
                    >
                      {triggerIngest.isPending ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <Play className="h-3 w-3 mr-1" />
                      )}
                      Fetch Now
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SourcesPage() {
  const [detailSource, setDetailSource] = useState<Source | null>(null)
  const { data: sources, isLoading, error } = useSources()

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Database className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Sources</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Manage intelligence feed sources
          </p>
        </div>
      </div>

      {/* Add source panel */}
      <AddSourcePanel />

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground text-sm">Loading sources...</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400">
          Failed to load sources: {error.message}
        </div>
      )}

      {/* Sources table */}
      {sources && sources.length > 0 && (
        <SourcesTable sources={sources} onViewDetail={setDetailSource} />
      )}

      {/* Empty state */}
      {sources && sources.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Database className="h-10 w-10 mb-3" />
          <p className="text-sm">No sources configured yet. Add one above to get started.</p>
        </div>
      )}

      {/* Detail modal */}
      <SourceDetailModal source={detailSource} onClose={() => setDetailSource(null)} />
    </div>
  )
}
